import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as d3 from 'd3-dsv';
import Immutable from 'immutable';

import { Icon, Spin, Table, Button } from 'antd';
import PropTypes from 'prop-types';

import { serverLoadOrganization, serverImportPOIs } from '../actions';

const loadingIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;

class OrganizationPOIUpload extends Component {
    constructor(props) {
        super(props);

        this.state = {
            csv: null,
            error: null
        }

        this.mandatoryColumns = this.mandatoryColumns.bind(this);
        this.inputTableColumns = this.inputTableColumns.bind(this);
        this.faultyTableColumns = this.faultyTableColumns.bind(this);
        this.hasLatLng = this.hasLatLng.bind(this);
        this.hasAddress = this.hasAddress.bind(this);
        this.getQualifiedName = this.getQualifiedName.bind(this);
        this.validateCsv = this.validateCsv.bind(this);

        this.componentDidMount = this.componentDidMount.bind(this);
        this.onTextRead = this.onTextRead.bind(this);
        this.onFileUpload = this.onFileUpload.bind(this);
        this.onUploadData = this.onUploadData.bind(this);
    }

    mandatoryColumns() {
        return ['name', 'street_address', 'suburb', 'postcode', 'town', 'state', 'country', 'lat', 'long'];
    }

    tableColumns() {
        return ['name', 'formatted_address', 'street_address', 'suburb', 'postcode', 'town', 'state', 'country', 'lat', 'long'];
    }

    inputTableColumns() {
        return this.tableColumns().map((name) => ({
            title: name,
            dataIndex: name,
            key: name,
        }));
    }

    faultyTableColumns() {
        return ['line', 'error', ...this.mandatoryColumns()].map((name) => ({
            title: name,
            dataIndex: name,
            key: name,
        }));
    }

    hasLatLng(row) {
        function isNumber(n) {
            return !isNaN(parseFloat(n)) && isFinite(n);
        }

        return isNumber(row.lat) && isNumber(row.long);
    }

    hasAddress(row) {
        return row.street_address && (row.suburb || row.state || row.town) && row.postcode && row.country;
    }

    getQualifiedName(row) {
        return `${row.name} - ${row.street_address}`;
    }

    validateCsv(csv) {
        const faultyRows = [];
        const qualifiedNames = new Set([]);

        csv.forEach((row, i) => {
            if (!this.hasLatLng(row) && !this.hasAddress(row)) {
                faultyRows.push({
                    key: i,
                    line: i + 2,
                    error: 'Missing full address or lat/long',
                    ...row
                });
                return;
            }

            const qualifiedName = this.getQualifiedName(row);
            if (qualifiedNames.has(qualifiedName)) {
                faultyRows.push({
                    key: i,
                    line: i + 2,
                    error: 'Duplicate name',
                    ...row
                });
                return;
            }

            qualifiedNames.add(qualifiedName);
        });

        return faultyRows;
    }

    componentDidMount() {
        let organizationId = this.props.match.params.organization_id;
        this.props.getOrganization(organizationId);
    }

    onFileUpload(e) {
        if (e.target.files.length > 0) {
            const fileReader = new FileReader();
            fileReader.onloadend = () => {
                this.onTextRead(fileReader.result);
            }
            fileReader.readAsText(e.target.files[0]);
        }
    }

    onTextRead(data) {
        const csv = d3.csvParse(data);

        const missingColumns = this.mandatoryColumns().filter((column) => !csv.columns.includes(column));

        const townMissing = missingColumns.findIndex(col => col === 'town');
        const stateMissing = missingColumns.findIndex(col => col === 'state');
        const suburbMissing = missingColumns.findIndex(col => col === 'suburb');

        if (missingColumns.length) {
            if (townMissing && stateMissing && suburbMissing) {
                this.setState(() => ({
                    csv: null,
                    error: {
                        message: 'Missing mandatory columns: ' + missingColumns.join(',')
                    }
                }));
                return;
            }
        }

        const faultyRows = this.validateCsv(csv);

        if (faultyRows.length) {
            this.setState(() => ({
                csv: null,
                error: {
                    faultyRows
                }
            }));
            return;
        }
        
        let parsedRows = [];
        for (let row of csv) {
            let formattedAddress = '';

            if (this.hasAddress(row)) {
                if (row.street_address) formattedAddress += `${row.street_address}, `;
                if (row.suburb) formattedAddress += row.suburb;
                if (!(row.town || row.state)) formattedAddress += ` ${row.postcode}, `;
                else {
                    formattedAddress += ', ';
                    formattedAddress += (row.town ? `${row.town} ${row.postcode}, ` : '');
                    formattedAddress += (row.state ? `${row.state} ${row.postcode}, ` : '');
                }
                formattedAddress += row.country;
            }
            parsedRows.push({ ...row, 'formatted_address': formattedAddress });
        }

        this.setState({
            csv: parsedRows,
            error: null
        });
    }

    onUploadData() {
        const nameCounts = new Map([]);

        this.state.csv.forEach((row) => {
            const name = row.name;
            if (!nameCounts.has(name)) {
                nameCounts.set(name, 0);
            }
            nameCounts.set(name, nameCounts.get(name) + 1);
        });

        const pois = [];

        this.state.csv.forEach((row) => {
            const name = (nameCounts.get(row.name) > 1) ? this.getQualifiedName(row) : row.name;

            let lat = null;
            let lng = null;
            if (this.hasLatLng(row)) {
                lat = parseFloat(row.lat);
                lng = parseFloat(row.long);
            }

            pois.push({
                formattedAddress: row.formatted_address,
                suburb: row.suburb,
                postcode: row.postcode,
                town: row.town || '',
                state: row.state,
                name,
                lat,
                lng
            });
        });

        const organizationId = this.props.match.params.organization_id;
        this.props.uploadPOIs(organizationId, pois);
    }

    render() {
        const organizationInfo = this.props.organizationsMap.get(this.props.match.params.organization_id);

        if (!organizationInfo) {
            return (
                <div style={{ marginTop: '10%', textAlign: 'center' }}>
                    <Spin indicator={loadingIcon} />
                </div>
            )
        }

        const csv = this.state.csv;
        const error = this.state.error;

        return (
            <div className="container-with-padding">
                <h4 className="heading-level-3">
                    {organizationInfo.name}
                </h4>
                <h4 className="heading-level-1" style={{ marginBottom: 16 }}>
                    Upload POIs
                </h4>

                {
                    error && (
                        <div>
                            {
                                error.message && <h4 style={{ color: 'red' }}>{this.state.error.message}</h4>
                            }
                            {
                                error.faultyRows &&
                                <div>
                                    <h4 style={{ color: 'red' }}>Validation failed. Faulty rows:</h4>
                                    <Table
                                        columns={this.faultyTableColumns()}
                                        dataSource={this.state.error.faultyRows}
                                        pagination={{ pageSize: 15 }}
                                    />
                                </div>
                            }
                        </div>

                    )
                }

                {
                    !csv &&
                    <div>
                        <input type="file" onChange={this.onFileUpload.bind(this)} accept="text/csv" />
                    </div>
                }

                {
                    csv &&
                    <div>
                        <h4>Input data ({this.state.csv.length} rows):</h4>
                        <Table
                            columns={this.inputTableColumns()}
                            dataSource={this.state.csv}
                            pagination={{ pageSize: 15 }}
                        />
                        <Button
                            type="primary"
                            icon="upload"
                            onClick={this.onUploadData}
                        >
                            Upload Data
                        </Button>
                    </div>
                }
            </div>
        )
    }
}

OrganizationPOIUpload.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            'organization_id': PropTypes.string,
        })
    }),
    organizationsMap: PropTypes.instanceOf(Immutable.Map),
    getOrganization: PropTypes.func,
    uploadPOIs: PropTypes.func,
}

// Map props to the store

const mapStateToProps = (state) => {
    return {
        organizationsMap: state.routes.organizations.server.getIn(['organizationsMap']),
    }
};

const mapDispatchToProps = (dispatch) => ({
    getOrganization: (organizationId) => {
        dispatch(serverLoadOrganization(organizationId));
    },
    uploadPOIs: (organizationId, pois) => {
        dispatch(serverImportPOIs(organizationId, pois));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationPOIUpload);


// WEBPACK FOOTER //
// ./src/organizations/components/OrganizationPOIUpload.js